import {
  Button,
  Checkbox,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  BudgetovacPositionSetRowType,
  type BudgetovacPositionsParentRow,
  type BudgetovacPositionsRow,
  BudgetovacPositionsSetsStore,
} from "@src/components/modules/budgets/positions-sets/store";
import {
  ACTIONS_COLUMN_ID,
  DataTable,
  LoadingOverlay,
  ModalConfirm,
  TruncatedTextWithTooltip,
} from "@src/components/ui-kit";
import type { BodyColumnDef } from "@src/components/ui-kit/DataTable/types";
import { Icon } from "@src/components/ui-kit/Icon";
import type { AppStore } from "@src/stores/AppStore";
import { FiltersPanelSearch } from "@src/utils/components/filters/components/filters-panel-search";
import { withCan } from "@src/utils/components/permissions";
import { denominate } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import { createContext, useContext } from "react";

export const BudgetPositionsSetsContext = createContext(
  {} as BudgetovacPositionsSetsStore,
);

export type BudgetPositionsSetsPageProps = {
  readonly clientId?: string;
};

export const BudgetPositionsSetsPage = withCan(
  { do: "integration_budgetovac" },
  observer(function BudgetPositionsSetsPage({
    clientId,
  }: BudgetPositionsSetsPageProps) {
    const appStore = useStore();
    const { store, columns } = useLocalObservable(() => ({
      store: new BudgetovacPositionsSetsStore(appStore, { clientId }),
      columns: createColumns(appStore),
    }));

    return (
      <BudgetPositionsSetsContext.Provider value={store}>
        <DataTable>
          <DataTable.NavBar>
            <HStack justify="space-between" py="3" spacing="2">
              <FiltersPanelSearch
                w="full"
                inputWidth="full"
                searchPlaceholder={t`Search by name`}
                onSearch={store.handleSearch}
              />

              <Button
                leftIcon={<Icon name="plus" />}
                onClick={store.openCreateModal}
                variant="outline"
              >
                <Trans>Add rate card</Trans>
              </Button>
            </HStack>
          </DataTable.NavBar>

          <LoadingOverlay
            isLoading={store.isLoading}
            position="absolute"
            size="md"
          >
            <DataTable.Body
              tableKey={store.tableKey}
              columns={columns}
              data={store.positionsSets}
            />
          </LoadingOverlay>
        </DataTable>

        <ModalConfirm
          destructive
          isOpen={store.deleteModalDisclosure.isOpen}
          onClose={store.deleteModalDisclosure.onClose}
          onConfirm={store.deletePositionsSet}
          isLoading={store.isDeleting}
          message={t`Are you sure you want to delete this rate card?`}
          confirmBtnTitle={t`Delete`}
          heading={`${t`Delete rate card`} ${store.getPositionsSetToDelete?.name}`}
        />
      </BudgetPositionsSetsContext.Provider>
    );
  }),
);

function isGroupedRow(
  row: BudgetovacPositionsRow,
): row is Extract<
  BudgetovacPositionsRow,
  { type: BudgetovacPositionSetRowType.Group }
> {
  return row.type === BudgetovacPositionSetRowType.Group;
}

function isParentRow(
  row: BudgetovacPositionsRow,
): row is Extract<
  BudgetovacPositionsRow,
  { type: BudgetovacPositionSetRowType.Row }
> {
  return row.type === BudgetovacPositionSetRowType.Row;
}

function createColumns(
  appStore: AppStore,
): BodyColumnDef<BudgetovacPositionsRow>[] {
  return [
    {
      id: "name",
      header: t`Name`,
      cell: ({ row }) =>
        isGroupedRow(row.original)
          ? (row.original.brand?.name ?? t`Default rate cards`)
          : row.original.name,
    },
    {
      id: "currency",
      header: t`Currency`,
      cell: ({ row }) =>
        isParentRow(row.original)
          ? row.original.currency_code
          : row.original.price,
    },
    {
      id: "default",
      header: t`Default`,
      cell: ({ row }) => {
        const store = useContext(BudgetPositionsSetsContext);

        if (!isParentRow(row.original)) return null;

        return (
          <Checkbox
            isChecked={row.original.active}
            onChange={({ target }) => {
              store.toggleActive(row.original.id, target.checked);
            }}
          />
        );
      },
    },
    {
      id: "commissions",
      header: t`Commissions`,
      cell: ({ row }) => {
        if (!isParentRow(row.original)) {
          return row.original.department_name;
        }

        /**
         * NOTE: More info on this denomination in
         * src/components/modules/budgets/positions-sets/components/modal/store.tsx:69
         */
        const { workspaceStore } = appStore;
        const denominator = workspaceStore.settings?.currency.denomination ?? 2;

        const commissions = [];

        if (row.original.internal_production_commission != null) {
          commissions.push(
            `${t`Internal`}: ${denominate(row.original.internal_production_commission, denominator)}%`,
          );
        }

        if (row.original.external_production_commission != null) {
          commissions.push(
            `${t`External`}: ${denominate(row.original.external_production_commission, denominator)}%`,
          );
        }

        if (row.original.media_budget_commission != null) {
          commissions.push(
            `${t`Media`}: ${denominate(row.original.media_budget_commission, denominator)}%`,
          );
        }

        if (row.original.extras_commission != null) {
          commissions.push(
            `${t`Royalties`}: ${denominate(row.original.extras_commission, denominator)}%`,
          );
        }

        return (
          <TruncatedTextWithTooltip text={commissions.join(", ") || "-"} />
        );
      },
    },
    {
      id: ACTIONS_COLUMN_ID,
      cell: ({ row }) => {
        const store = useContext(BudgetPositionsSetsContext);

        if (!isParentRow(row.original)) return null;

        return (
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<Icon name="dots-vertical" />}
              variant="ghost"
            />
            <MenuList>
              <MenuItem
                icon={<Icon name="edit-02" />}
                onClick={() => {
                  store.openEditModal(
                    row.original as BudgetovacPositionsParentRow,
                  );
                }}
              >
                <Trans>Edit</Trans>
              </MenuItem>
              <MenuItem
                icon={<Icon name="copy-02" />}
                onClick={() => {
                  store.openDuplicateModal(
                    row.original as BudgetovacPositionsParentRow,
                  );
                }}
              >
                <Trans>Duplicate</Trans>
              </MenuItem>
              <MenuItem
                icon={<Icon color="red.500" name="trash-03" />}
                onClick={() => {
                  store.deleteModalDisclosure.onOpen({ id: row.original.id });
                }}
              >
                <Trans>Delete</Trans>
              </MenuItem>
            </MenuList>
          </Menu>
        );
      },
    },
  ];
}
